import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { SelectField } from '@retailer-platform/shared-components/src/tds'
import { CloseIcon } from '@instacart/ids-core'
import { useDashMessages } from '../../../../intl/intl.hooks'
import type { ConversationHistoryItem } from '../utils/conversationHistory.types'

interface ConversationSelectorProps {
  conversations: ConversationHistoryItem[]
  currentConversationId: string | null
  onSelectConversation: (conversationId: string) => void
  onClearAllConversations: () => void
}

interface ConversationOption {
  value: string
  label: string
  leftIcon?: React.ComponentType
  isClearAllAction?: boolean
}

const SelectorContainer = styled.div({
  width: '220px',
})

export const ConversationSelector: React.FC<ConversationSelectorProps> = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onClearAllConversations,
}) => {
  const intlMessages = useDashMessages({
    clearAllConversations: 'aiAgent.conversationSelector.clearAllConversations',
    selectConversationPlaceholder: 'aiAgent.conversationSelector.placeholder',
    newConversation: 'aiAgent.conversationSelector.newConversation',
  })

  const options = useMemo((): ConversationOption[] => {
    // Sort conversations by most recent first (createdAt descending)
    const sortedConversations = [...conversations].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    const conversationOptions: ConversationOption[] = sortedConversations.map(conv => ({
      value: conv.id,
      label: conv.title,
    }))

    const options: ConversationOption[] = [
      {
        value: 'clear-all',
        label: intlMessages.clearAllConversations,
        leftIcon: CloseIcon,
        isClearAllAction: true,
      },
    ]

    // Add current conversation option if there's an active conversation not in history
    if (currentConversationId && !conversations.find(conv => conv.id === currentConversationId)) {
      options.push({
        value: currentConversationId,
        label: intlMessages.newConversation,
      })
    }

    return [...options, ...conversationOptions]
  }, [
    conversations,
    intlMessages.clearAllConversations,
    intlMessages.newConversation,
    currentConversationId,
  ])

  const currentValue = useMemo((): string => {
    if (currentConversationId) {
      const existingConversation = conversations.find(conv => conv.id === currentConversationId)
      if (existingConversation) {
        return existingConversation.id
      }
      // Current conversation (not yet saved) - return the conversation ID so it matches our option
      return currentConversationId
    }

    // No active conversation
    return ''
  }, [conversations, currentConversationId])

  const handleChange = (value: string | null) => {
    if (!value) return

    const selectedOption = options.find(option => option.value === value)
    if (!selectedOption) return

    if (selectedOption.isClearAllAction) {
      onClearAllConversations()
    } else if (value !== currentConversationId) {
      onSelectConversation(value)
    }
  }

  return (
    <SelectorContainer>
      <SelectField
        width="180px"
        searchable
        value={currentValue}
        options={options}
        onChange={handleChange}
        placeholder={intlMessages.selectConversationPlaceholder}
      />
    </SelectorContainer>
  )
}
